import { useCallback, useMemo, useState } from "react";
import { Alert, Form, Input, message, Row, Checkbox, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { Button } from "Components/General/Button";
import { PathDirectorySelect } from "Components/Form/PathDirectorySelect";
import { I18N_NS } from "Config/i18n";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { projectsDataStore } from "Stores";
import { dirnameCharValidator } from "utils/inputValidation";

export interface CopyDirectoryModalProps extends ModalComponentProps {
  copyDirectory: (value: API_FolderCopyRequest) => Promise<API_FolderCopyResponse>;
  sourceId: number;
  initialFolderName: string;
  canCopyMetadata: boolean;
  canCopyAccessRights: boolean;
}

const errorMessageConfig: {
  [key in API_FOLDERCOPYRESPONSE_STATUS]?: { title: string; text: string; canSkip?: boolean };
} = {
  NO_ACCESS: {
    title: "You do not have access to all parts of the source structure",
    text: "Do you want to skip folders without sufficient permissions?",
    canSkip: true,
  },
  CONTAINS_EXTERNAL_SYMLINKS: {
    title: "There are external linked directories found in the folder structure",
    text: "Do you want to abort the copy process or copy the structure without the external folders?",
    canSkip: true,
  },
  NO_SYMLINK_ACCESS: {
    title: "Directory structure contains linked directories and therefore cannot be copied",
    text: "",
    canSkip: true,
  },
};

const getMissingPermissionsMessage = (canCopyAccessRights: boolean, canCopyMetadata: boolean) => {
  if (!canCopyAccessRights && !canCopyMetadata) {
    // eslint-disable-next-line max-len
    return "Access rights and metadata are not copied, because you do not have sufficient permissions. They have to be applied by a project administrator.";
  }
  if (!canCopyAccessRights) {
    // eslint-disable-next-line max-len
    return "Access rights are not copied, because you do not have sufficient permissions. They have to be applied by a project administrator.";
  }
  if (!canCopyMetadata) {
    // eslint-disable-next-line max-len
    return "Metadata is not copied, because you do not have sufficient permissions. It has to be applied by a project administrator.";
  }
  return null;
};

export const CopyDirectoryModal: React.FC<CopyDirectoryModalProps> = ({
  hideModal,
  copyDirectory,
  sourceId,
  initialFolderName,
  canCopyMetadata,
  canCopyAccessRights,
}) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const [copyErrorStatuses, setCopyErrorStatuses] = useState<API_FOLDERCOPYRESPONSE_STATUS[]>([]);
  const [confirmedErrorStatuses, setConfirmedErrorStatuses] = useState<API_FOLDERCOPYRESPONSE_STATUS[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const shownCopyErrorStatuses = useMemo(
    () => copyErrorStatuses.filter((s) => !confirmedErrorStatuses.includes(s)),
    [confirmedErrorStatuses, copyErrorStatuses]
  );

  const skipStatus = (s: API_FOLDERCOPYRESPONSE_STATUS) => {
    setConfirmedErrorStatuses(confirmedErrorStatuses.concat(s));
  };

  const missingPermissionsMessage = useMemo(
    () => getMissingPermissionsMessage(canCopyAccessRights, canCopyMetadata),
    [canCopyAccessRights, canCopyMetadata]
  );

  const folderNameValidator = useCallback((_: unknown, value: string) => dirnameCharValidator(_, value, t), [t]);

  const onFinish = async (value: API_FolderCopyRequest) => {
    const requestObject: API_FolderCopyRequest = {
      withSymlinkDirs: false,
      withRights: false,
      withMetadata: false,
      ...value,
      sourceId,
      skipNoAccess: confirmedErrorStatuses.includes("NO_ACCESS"),
      skipExternalLinks: confirmedErrorStatuses.includes("CONTAINS_EXTERNAL_SYMLINKS"),
    };
    try {
      setLoading(true);
      const response = await copyDirectory(requestObject);
      if (response.status[0] === "OK") {
        message.success(t("Success"));
        hideModal();
      } else if (response.status.includes("NO_SYMLINK_ACCESS")) {
        setCopyErrorStatuses(response.status.slice());
      } else {
        setCopyErrorStatuses(response.status.slice());
      }
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const initialValue = projectsDataStore.currentProject.directoryAutonumbering
    ? initialFolderName.replace(/^[\d.]+\s/, "")
    : initialFolderName;

  return (
    <Form requiredMark={true} onFinish={onFinish}>
      <Form.Item noStyle>Original: {initialFolderName}</Form.Item>
      <Form.Item
        rules={[
          { required: true, message: t("This field is required") },
          { required: true, validator: folderNameValidator },
        ]}
        labelCol={{ span: 24 }}
        label={t("Name of the new directory")}
        initialValue={initialValue}
        name="newDirName"
      >
        <Input
          allowClear={true}
          autoFocus={true}
          suffix={
            <Tooltip
              title={t("Unallowed characters: {{unallowedChars}}", {
                unallowedChars: t('/\\:*?"<>|@&%'),
                interpolation: { escapeValue: false },
              })}
            >
              <span>
                <FontAwesomeIcon icon={["far", "info-circle"]} color="#0fb9b1" fixedWidth={true} />
              </span>
            </Tooltip>
          }
        />
      </Form.Item>
      <Form.Item
        rules={[{ required: true, message: t("This field is required") }]}
        labelCol={{ span: 24 }}
        label={t("Destination directory")}
        name="targetId"
      >
        <PathDirectorySelect />
      </Form.Item>
      {canCopyAccessRights && (
        <Form.Item name="withRights" valuePropName="checked" initialValue={false} style={{ margin: 0 }}>
          <Checkbox>{t("Copy all access rights")}</Checkbox>
        </Form.Item>
      )}
      {canCopyMetadata && (
        <Form.Item name="withMetadata" valuePropName="checked" initialValue={true} style={{ margin: 0 }}>
          <Checkbox>{t("Copy all metadata")}</Checkbox>
        </Form.Item>
      )}
      <Form.Item name="withDescription" valuePropName="checked" initialValue={true} style={{ margin: 0 }}>
        <Checkbox>{t("Copy all descriptions")}</Checkbox>
      </Form.Item>
      {projectsDataStore.isAdmin && (
        <Form.Item name="withSymlinkDirs" valuePropName="checked" initialValue={true}>
          <Checkbox>{t("Copy linked directories")}</Checkbox>
        </Form.Item>
      )}
      {missingPermissionsMessage && (
        <Alert
          type="info"
          className="mt-2"
          title={t(missingPermissionsMessage)}
          showIcon
          icon={<FontAwesomeIcon icon={["far", "info-circle"]} />}
        />
      )}
      {shownCopyErrorStatuses.map((status) => (
        <Alert
          key={status}
          type="warning"
          className="mb-2"
          title={t(errorMessageConfig[status].title)}
          description={
            errorMessageConfig[status].text && (
              <Row>
                <div className="mb-md">{t(errorMessageConfig[status].text)}</div>
                {errorMessageConfig[status].canSkip && (
                  <Row justify="end" className="mt-md flex1">
                    {/* <Button danger type="default" className="mr-md" onClick={hideModal}>
                  {t("Cancel")}
                  </Button> */}
                    <Button htmlType="button" type="primary" onClick={() => skipStatus(status)}>
                      {t("Skip")}
                    </Button>
                  </Row>
                )}
              </Row>
            )
          }
          showIcon
          icon={<FontAwesomeIcon icon={["far", "exclamation-triangle"]} />}
        />
      ))}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
        <Button style={{ marginRight: 10 }} onClick={hideModal}>
          {t("Cancel")}
        </Button>
        <Button htmlType="submit" type="primary" loading={loading} disabled={shownCopyErrorStatuses.length > 0}>
          {t("Copy")}
        </Button>
      </div>
    </Form>
  );
};
